'use client';

import {
  Background,
  BackgroundVariant,
  type ColorMode,
  Controls,
  type Edge,
  type EdgeTypes,
  MiniMap,
  type Node,
  type NodeTypes,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { forwardRef, useCallback, useRef, type ReactNode } from 'react';
import { useThemeMode } from '@terra/ui-design-system';
import { useAutoLayout, type Direction } from './useAutoLayout';

export type FlowGraphBaseProps = {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  nodeTypes: NodeTypes;
  edgeTypes: EdgeTypes;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onPaneClick?: () => void;
  onNodeDrag?: (event: React.MouseEvent, node: Node) => void;
  onGetNodeColor?: (node: Node) => string;
  backgroundColor?: string;
  minZoom?: number;
  maxZoom?: number;
  width?: number;
  height?: number;
  layoutDirection?: Direction;
  nodeSeparation?: number;
  rankSeparation?: number;
  onFlowInit?: (instance: ReactFlowInstance) => void;
  children?: ReactNode;
};

export const FlowGraphBase = forwardRef<HTMLDivElement, FlowGraphBaseProps>(
  function FlowGraphBase(
    {
      nodes,
      edges,
      onNodesChange,
      onEdgesChange,
      nodeTypes,
      edgeTypes,
      onNodeClick,
      onPaneClick,
      onNodeDrag,
      onGetNodeColor,
      backgroundColor = 'var(--colors-gray-6)',
      minZoom = 0.3,
      maxZoom = 7,
      width,
      height,
      layoutDirection = 'LR',
      nodeSeparation = 200,
      rankSeparation = 200,
      onFlowInit,
      children,
    },
    ref,
  ) {
    const { mode: themeMode } = useThemeMode();
    const flowRef = useRef<ReactFlowInstance | null>(null);

    const fitViewToNodes = useCallback(() => {
      setTimeout(() => {
        flowRef.current?.fitView({ duration: 0, padding: 0.1 });
      }, 100);
    }, []);

    useAutoLayout({
      direction: layoutDirection,
      nodeSeparation,
      rankSeparation,
      onLayoutComplete: fitViewToNodes,
    });

    const onInit = useCallback(
      (instance: ReactFlowInstance) => {
        flowRef.current = instance;
        onFlowInit?.(instance);
      },
      [onFlowInit],
    );

    return (
      <div
        ref={ref}
        style={{
          position: 'relative',
          height: height ? `${height}px` : '100%',
          width: width ? `${width}px` : '100%',
        }}
      >
        {children}
        <ReactFlow
          proOptions={{ hideAttribution: true }}
          colorMode={themeMode as ColorMode}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={onInit}
          minZoom={minZoom}
          maxZoom={maxZoom}
          attributionPosition="bottom-left"
          defaultEdgeOptions={{ type: 'smoothstep', animated: false }}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onNodeDrag={onNodeDrag}
        >
          <Background
            variant={BackgroundVariant.Dots}
            color={backgroundColor}
            gap={12}
          />
          <Controls />
          <MiniMap
            nodeStrokeWidth={0}
            nodeColor={onGetNodeColor}
            zoomable
            pannable
            position="bottom-right"
          />
        </ReactFlow>
      </div>
    );
  },
);
